import React, { useState, useMemo, useEffect, useId } from "react";
import {
  GraduationCap,
  ShieldAlert,
  Activity,
  CalendarDays,
  HeartPulse,
  Brain,
  Target,
  Eye,
  Sparkles,
  Users,
  Trophy,
} from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarRadiusAxis,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTheme } from "../context/themeContext";
import { BRAND, TOKENS, StatPill, CardShell } from "../pages/Test/dashboardShared";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

function polarToCartesian(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function donutArcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, rOuter, endAngle);
  const endOuter = polarToCartesian(cx, cy, rOuter, startAngle);
  const startInner = polarToCartesian(cx, cy, rInner, endAngle);
  const endInner = polarToCartesian(cx, cy, rInner, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${endInner.x} ${endInner.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${startInner.x} ${startInner.y}`,
    "Z",
  ].join(" ");
}

function AnimatedNumber({ value, suffix = "", duration = 700 }) {
  const numeric = typeof value === "number" && !Number.isNaN(value);
  const [display, setDisplay] = useState(numeric ? 0 : value);

  useEffect(() => {
    if (!numeric) {
      setDisplay(value);
      return;
    }
    let frame;
    const start = performance.now();
    const to = value;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(to * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => frame && cancelAnimationFrame(frame);
  }, [value, duration, numeric]);

  if (!numeric) return <>{display}</>;
  return (
    <>
      {display.toFixed(0)}
      {suffix}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* palette (same family as the rest of the dashboard)                  */
/* ------------------------------------------------------------------ */

const IIT_TONE = {
  good: "#22C55E",
  mild: "#3B82F6",
  watch: "#F59E0B",
  concern: "#F97316",
  critical: "#EF4444",
};

const IIT_GRAD = {
  good: ["#86efac", "#22C55E"],
  mild: ["#93c5fd", "#3B82F6"],
  watch: ["#ffd27f", "#F59E0B"],
  concern: ["#fdba74", "#F97316"],
  critical: ["#fca5a5", "#EF4444"],
};

const IIT_TIER_META = {
  low: { label: "Steady", tone: "good", hint: "No elevated indicators" },
  moderate: { label: "Monitor", tone: "watch", hint: "Some elevated indicators" },
  high: { label: "Elevated", tone: "concern", hint: "Proactive outreach advised" },
  priority: { label: "Priority", tone: "critical", hint: "Immediate follow-up advised" },
};

const IIT_ARCH_COLORS = ["#8b7cf6", "#ff9ecf", "#7fe3d0", "#ffd27f", "#7dd3fc"];

const toneForPct = (p) =>
  p >= 50 ? "critical" : p >= 30 ? "concern" : p >= 15 ? "watch" : "good";

/* ------------------------------------------------------------------ */
/* building blocks                                                     */
/* ------------------------------------------------------------------ */

function IitCard({ accent = "teal", title, subtitle, icon: Icon, right, children, className = "" }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <CardShell accent={accent} className={"ta-card-lift flex flex-col " + className}>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {Icon && (
            <span
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{ background: `${BRAND.purple}1f`, color: BRAND.purple }}
            >
              <Icon size={18} />
            </span>
          )}
          <div>
            <h3 className={"text-xl font-bold " + t.textPrimary}>{title}</h3>
            {subtitle && <p className={"mt-0.5 text-sm " + t.textMuted}>{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </CardShell>
  );
}

/** Gradient progress ring with glow (same look as PremiumDial) */
function IitRing({ pct, tone, size = 84, stroke = 7, active, children }) {
  const uid = useId().replace(/:/g, "");
  const [from, to] = IIT_GRAD[tone] ?? IIT_GRAD.good;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, pct)) / 100) * c;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{
          overflow: "visible",
          filter: active ? `drop-shadow(0 0 8px ${to}99)` : "none",
          transition: "filter .3s ease",
        }}
      >
        <defs>
          <linearGradient id={`ring-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(148,130,255,0.12)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#ring-${uid})`}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .9s cubic-bezier(.34,1.56,.64,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ?? (
          <span className="text-sm font-bold tabular-nums" style={{ color: to }}>
            <AnimatedNumber value={pct} suffix="%" />
          </span>
        )}
      </div>
    </div>
  );
}

/** Donut with hover glow (same look as the test-result donuts) */
function IitDonut({ bands, centerValue, centerLabel, size = 176 }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(null);
  const total = bands.reduce((s, b) => s + b.count, 0);
  let cursor = 0;
  const arcs = bands
    .filter((b) => b.count > 0)
    .map((b) => {
      const start = total ? (cursor / total) * 360 : 0;
      cursor += b.count;
      let end = total ? (cursor / total) * 360 : 0;
      if (end - start >= 360) end = start + 359.99;
      return { ...b, start, end, color: IIT_TONE[b.tone] };
    });
  const h = arcs.find((a) => a.label === hv);
  const big = size >= 140;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 200 200" className="h-full w-full">
        <circle cx="100" cy="100" r="82" fill="none" stroke={t.donutRing} strokeWidth="1" />
        {arcs.length === 0 && (
          <circle cx="100" cy="100" r="69" fill="none" stroke="rgba(148,130,255,0.12)" strokeWidth="26" />
        )}
        {arcs.map((a) => (
          <path
            key={a.label}
            d={donutArcPath(100, 100, 82, 56, a.start, a.end)}
            fill={a.color}
            opacity={hv && hv !== a.label ? 0.35 : 1}
            style={{
              cursor: "pointer",
              filter: hv === a.label ? `drop-shadow(0 0 6px ${a.color}aa)` : "none",
              transition: "opacity .25s ease, filter .25s ease, transform .25s ease",
              transform: hv === a.label ? "scale(1.03)" : "scale(1)",
              transformOrigin: "100px 100px",
            }}
            onMouseEnter={() => setHv(a.label)}
            onMouseLeave={() => setHv(null)}
          />
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={(big ? "text-3xl" : "text-lg") + " font-bold tabular-nums transition-colors"}
          style={{ color: h ? h.color : undefined }}
        >
          <span className={h ? "" : t.textPrimary}>{h ? `${h.pct}%` : centerValue}</span>
        </span>
        <span
          className={
            "mt-0.5 max-w-[6rem] text-center font-semibold uppercase leading-tight tracking-wide " +
            (big ? "text-[11px] " : "text-[8px] ") +
            t.textMuted
          }
        >
          {h ? h.label : centerLabel}
        </span>
      </div>
    </div>
  );
}

function IitLegend({ bands }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {bands.map((b) => {
        const c = IIT_TONE[b.tone];
        return (
          <span
            key={b.label}
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ background: `${c}1a`, color: c }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
            {b.label}
            <b className="tabular-nums">{b.count}</b>
          </span>
        );
      })}
    </div>
  );
}

/** Vertical gradient columns (same idea as the Syndrome bar chart) */
function IitColumns({ bands }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(null);
  const max = Math.max(...bands.map((b) => b.pct), 1);

  return (
    <div className="flex h-44 items-end gap-2">
      {bands.map((b, i) => {
        const [from, to] = IIT_GRAD[b.tone];
        const active = hv === b.label;
        return (
          <div
            key={b.label}
            className="flex h-full flex-1 cursor-pointer flex-col items-center justify-end gap-1"
            style={{ opacity: hv && !active ? 0.5 : 1, transition: "opacity .2s ease" }}
            onMouseEnter={() => setHv(b.label)}
            onMouseLeave={() => setHv(null)}
          >
            <span className="text-xs font-bold tabular-nums" style={{ color: to }}>
              {b.pct}%
            </span>
            <div className="flex w-full flex-1 items-end rounded-xl" style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
              <div
                className="ta-stagger w-full rounded-xl"
                style={{
                  height: `${Math.max(6, (b.pct / max) * 100)}%`,
                  background: `linear-gradient(180deg, ${to}, ${from})`,
                  boxShadow: active ? `0 0 12px ${to}88` : "none",
                  transition: "box-shadow .2s ease",
                  animationDelay: `${i * 60}ms`,
                }}
              />
            </div>
            <span className={"text-center text-[10px] font-semibold leading-tight " + t.textPrimary}>
              {b.label}
            </span>
            <span className={"text-[10px] tabular-nums " + t.textMuted}>{b.count}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* sections                                                            */
/* ------------------------------------------------------------------ */

function IitHero({ data }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const tiers = Object.fromEntries((data.tiers ?? []).map((x) => [x.key, x]));
  const flagged = (tiers.high?.count ?? 0) + (tiers.priority?.count ?? 0);
  const flaggedPct = data.totalParticipants ? Math.round((flagged / data.totalParticipants) * 100) : 0;
  const k10 = data.screening?.find((s) => s.key === "K10");
  const phq = data.screening?.find((s) => s.key === "PHQ-9");

  return (
    <CardShell accent="purple" className="ta-card-lift flex flex-col">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
            style={{ color: BRAND.purple }}
          >
            <GraduationCap size={14} />
            IIT Bombay · Student wellbeing
          </div>
          <h2 className={"text-3xl font-extrabold tracking-tight " + t.textPrimary}>
            Cohort wellbeing insights
          </h2>
          <p className={"mt-2 max-w-2xl text-sm " + t.textMuted}>
            {data.totalParticipants > 0
              ? `${flaggedPct}% of assessed students (${flagged} of ${data.totalParticipants}) are in the Elevated or Priority range and would benefit from proactive outreach.`
              : "No students have completed the assessment yet."}
          </p>
        </div>
      
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatPill
          icon={Users}
          tint={BRAND.purple}
          value={<AnimatedNumber value={data.totalParticipants} />}
          label="Students assessed"
        />
        <StatPill
          icon={ShieldAlert}
          tint={BRAND.pink}
          value={<AnimatedNumber value={flagged} />}
          label="Need follow-up"
        />
        <StatPill icon={Activity} tint={BRAND.teal} value={`${k10?.avg ?? 0}/50`} label="Average K10" />
        <StatPill icon={HeartPulse} tint="#f5a623" value={`${phq?.avg ?? 0}/27`} label="Average PHQ-9" />
      </div>
    </CardShell>
  );
}

function IitTierOverview({ tiers, total }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(null);

  return (
    <IitCard
      accent="pink"
      icon={ShieldAlert}
      title="Overall wellbeing status"
      subtitle="Each student sits in one tier, based on PHQ-9, K10, entrapment, burdensomeness and current distress"
    >
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiers.map((x, i) => {
          const m = IIT_TIER_META[x.key];
          const c = IIT_TONE[m.tone];
          return (
            <div
              key={x.key}
              onMouseEnter={() => setHv(x.key)}
              onMouseLeave={() => setHv(null)}
              className={
                "ta-stagger flex flex-col items-center rounded-2xl border px-3 py-5 text-center transition-all duration-300 " +
                t.pillBorder +
                " " +
                t.surfaceSoft
              }
              style={{
                animationDelay: `${i * 70}ms`,
                transform: hv === x.key ? "translateY(-3px)" : "none",
                boxShadow: hv === x.key ? `0 14px 30px -14px ${c}88` : "none",
              }}
            >
              <IitRing pct={x.pct} tone={m.tone} size={108} stroke={9} active={hv === x.key}>
                <span className="text-2xl font-extrabold tabular-nums" style={{ color: c }}>
                  <AnimatedNumber value={x.count} />
                </span>
                <span className={"text-[10px] font-semibold " + t.textMuted}>{x.pct}%</span>
              </IitRing>
              <div className={"mt-3 text-sm font-bold " + t.textPrimary}>{m.label}</div>
              <div className={"text-[11px] " + t.textMuted}>{m.hint}</div>
            </div>
          );
        })}
      </div>
      {total > 0 && (
        <p className={"mt-4 text-center text-[11px] " + t.textFaint}>
          Out of {total} students (latest submission per student)
        </p>
      )}
    </IitCard>
  );
}

function IitInsightCard({ insight, index }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(false);
  const tone = insight.pct >= 30 ? "critical" : insight.pct >= 15 ? "concern" : "watch";
  const color = IIT_TONE[tone];

  return (
    <div
      onMouseEnter={() => setHv(true)}
      onMouseLeave={() => setHv(false)}
      className={
        "ta-stagger flex items-center gap-4 rounded-2xl border p-4 transition-all duration-300 " +
        t.pillBorder +
        " " +
        t.surfaceSoft
      }
      style={{
        animationDelay: `${index * 50}ms`,
        transform: hv ? "translateY(-2px)" : "none",
        boxShadow: hv ? `0 12px 26px -14px ${color}88` : "none",
      }}
    >
      <IitRing pct={insight.pct} tone={tone} size={72} stroke={7} active={hv} />
      <div className="min-w-0">
        <div className={"text-sm font-bold " + t.textPrimary}>{insight.title}</div>
        <div className={"mt-0.5 text-xs leading-snug " + t.textMuted}>{insight.desc}</div>
        <span
          className="mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums"
          style={{ background: `${color}1f`, color }}
        >
          {insight.count} {insight.count === 1 ? "student" : "students"}
        </span>
      </div>
    </div>
  );
}

function IitConcernItem({ m, rank, index }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(false);
  const tone = toneForPct(m.atRiskPct);
  const color = IIT_TONE[tone];

  return (
    <div
      onMouseEnter={() => setHv(true)}
      onMouseLeave={() => setHv(false)}
      className="ta-stagger relative flex cursor-default flex-col items-center rounded-2xl px-2 py-3 text-center transition-all duration-300"
      style={{
        animationDelay: `${index * 35}ms`,
        background: hv ? (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)") : "transparent",
      }}
    >
      {rank <= 3 && (
        <span
          className="absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold"
          style={{ background: `${color}22`, color }}
        >
          {rank === 1 ? <Trophy size={10} /> : rank}
        </span>
      )}
      <IitRing pct={m.atRiskPct} tone={tone} size={84} stroke={7} active={hv} />
      <span className={"mt-2 text-xs font-semibold leading-tight " + t.textPrimary}>{m.label}</span>
      <span className={"text-[11px] tabular-nums " + t.textMuted}>
        {m.atRisk} of {m.answered}
      </span>
      {m.protective && (
        <span
          className="mt-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
          style={{ background: `${BRAND.teal}22`, color: BRAND.teal }}
        >
          protective
        </span>
      )}
    </div>
  );
}

function IitTopConcerns({ measures }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const ranked = useMemo(
    () =>
      [...measures]
        .filter((m) => m.answered > 0)
        .sort((a, b) => b.atRiskPct - a.atRiskPct),
    [measures],
  );
  const colorFor = (p) =>
    p >= 50
      ? IIT_TONE.critical
      : p >= 30
        ? IIT_TONE.concern
        : p >= 15
          ? IIT_TONE.watch
          : IIT_TONE.good;
  return (
    <IitCard
      accent="pink"
      icon={Target}
      title="Where students struggle most"
      subtitle="Share of students in the concern range for each measure (for protective factors, a low score is the concern)"
    >
      <div className="flex flex-col gap-4">
        {ranked.map((m, i) => {
          const c = colorFor(m.atRiskPct);
          return (
            <div
              key={m.key}
              className="ta-stagger ta-hoverable"
              style={{ animationDelay: `${i * 35}ms` }}
            >
              <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
                <span
                  className={
                    "flex items-center gap-2 font-medium " +
                    (isDark ? "text-slate-200" : "text-slate-700")
                  }
                >
                  {m.label}
                  {m.protective && (
                    <span
                      className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                      style={{
                        background: `${BRAND.teal}22`,
                        color: BRAND.teal,
                      }}
                    >
                      protective
                    </span>
                  )}
                </span>
                <span className="font-bold tabular-nums" style={{ color: c }}>
                  {m.atRisk}
                  <span
                    className={"ml-1.5 text-xs font-semibold " + t.textMuted}
                  >
                    ({m.atRiskPct}%)
                  </span>
                </span>
              </div>
              <div
                className={
                  "h-2.5 w-full overflow-hidden rounded-full " + t.track
                }
              >
                <div
                  className="ta-premium-bar h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${m.atRiskPct}%`,
                    background: `linear-gradient(90deg, ${c}99, ${c})`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </IitCard>
  );
}


function IitScreening({ screening }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const sub = {
    K10: "Psychological distress over the past month",
    "PHQ-9": "Depressive symptoms over the past two weeks",
  };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {screening.map((s, i) => (
        <IitCard
          key={s.key}
          accent={i === 0 ? "purple" : "teal"}
          icon={i === 0 ? Activity : HeartPulse}
          title={s.label}
          subtitle={sub[s.key]}
        >
          <div className="flex flex-col items-center gap-5">
            <IitDonut bands={s.bands} centerValue={`${s.avg}`} centerLabel={`avg of ${s.max}`} size={190} />
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
              {s.bands.map((b) => (
                <div
                  key={b.label}
                  className={"flex items-center justify-between gap-3 rounded-xl px-3 py-2 " + t.surfaceSoft}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: IIT_TONE[b.tone], boxShadow: `0 0 8px ${IIT_TONE[b.tone]}aa` }}
                    />
                    <span className={isDark ? "text-slate-200" : "text-slate-700"}>{b.label}</span>
                  </span>
                  <span className={"text-sm font-semibold tabular-nums " + t.textMuted}>
                    {b.count}
                    <span className="ml-1.5 opacity-70">({b.pct}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </IitCard>
      ))}
    </div>
  );
}

function IitArchetypes({ archetypes }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);
  const colorOf = (name) => IIT_ARCH_COLORS[archetypes.findIndex((a) => a.name === name) % IIT_ARCH_COLORS.length];
  const data = archetypes.map((a) => ({ label: a.name, value: a.pct, count: a.count }));
  const maxPct = Math.max(...data.map((d) => d.value), 30);

  return (
    <IitCard accent="pink" icon={Sparkles} title="Personality styles" subtitle="Dominant style across the cohort">
      <div className="h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="68%">
            <PolarGrid stroke={t.gridStroke} />
            <PolarRadiusAxis domain={[0, maxPct]} tick={false} axisLine={false} tickLine={false} />
            <PolarAngleAxis
              dataKey="label"
              tick={({ payload, x, y, cx, cy }) => {
                const isH = hovered === payload.value;
                return (
                  <text
                    x={x + (x - cx) * 0.1}
                    y={y + (y - cy) * 0.1}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={isH ? 12.5 : 11}
                    fontWeight={isH ? 800 : 600}
                    fill={isH ? colorOf(payload.value) : t.axisTick}
                    style={{ transition: "all .15s ease" }}
                  >
                    {payload.value}
                  </text>
                );
              }}
            />
            <Tooltip
              wrapperStyle={{ zIndex: 9999, pointerEvents: "none" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div
                    className="rounded-xl border px-3 py-2 text-xs shadow-xl"
                    style={{ background: t.tooltipBg, borderColor: t.tooltipBorder }}
                  >
                    <div className="font-semibold" style={{ color: colorOf(p.label) }}>
                      {p.label}
                    </div>
                    <div className={isDark ? "text-slate-300" : "text-slate-600"}>
                      {p.count} students · {p.value}%
                    </div>
                  </div>
                );
              }}
            />
            <Radar
              dataKey="value"
              stroke={BRAND.purple}
              strokeWidth={2}
              fill="url(#iitRadarFill)"
              fillOpacity={1}
              dot={({ cx, cy, payload }) =>
                cx == null ? null : (
                  <circle
                    key={payload.label}
                    cx={cx}
                    cy={cy}
                    r={hovered === payload.label ? 6 : 4}
                    fill={colorOf(payload.label)}
                    stroke={hovered === payload.label ? "#fff" : "none"}
                    strokeWidth={2}
                  />
                )
              }
            />
            <defs>
              <linearGradient id="iitRadarFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={BRAND.purple} stopOpacity={0.32} />
                <stop offset="100%" stopColor={BRAND.pink} stopOpacity={0.12} />
              </linearGradient>
            </defs>
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex flex-wrap justify-center gap-2">
        {archetypes.map((a, i) => {
          const c = IIT_ARCH_COLORS[i % IIT_ARCH_COLORS.length];
          return (
            <button
              key={a.name}
              onMouseEnter={() => setHovered(a.name)}
              onMouseLeave={() => setHovered(null)}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all"
              style={{
                background: `${c}${hovered === a.name ? "33" : "1a"}`,
                color: c,
              }}
            >
              {i === 0 && <Trophy size={11} />}
              {a.name}
              <b className="tabular-nums">
                {a.count} · {a.pct}%
              </b>
            </button>
          );
        })}
      </div>
    </IitCard>
  );
}

function IitMeasureCard({ m, index }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const tone = toneForPct(m.atRiskPct);
  const color = IIT_TONE[tone];
  return (
    <div
      className={
        "ta-stagger flex items-center gap-4 rounded-2xl border p-4 " + t.pillBorder + " " + t.surfaceSoft
      }
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <IitDonut bands={m.bands} centerValue={m.avg} centerLabel={`of ${m.max}`} size={104} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={"text-sm font-bold " + t.textPrimary}>{m.label}</span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ background: `${color}1f`, color }}
          >
            {m.atRiskPct}% concern
          </span>
        </div>
        <div className={"mb-2 mt-0.5 text-[11px] " + t.textMuted}>
          {m.protective ? "Higher is healthier" : "Lower is healthier"}
        </div>
        <IitLegend bands={m.bands} />
      </div>
    </div>
  );
}

function IitDeepDive({ measures }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const groups = useMemo(() => [...new Set(measures.map((m) => m.group))], [measures]);
  const [tab, setTab] = useState(groups[0]);
  const active = groups.includes(tab) ? tab : groups[0];
  const list = measures.filter((m) => m.group === active);

  return (
    <IitCard
      accent="teal"
      icon={Brain}
      title="Measure-by-measure breakdown"
      subtitle="How students are distributed across levels for every dimension"
    >
      <div className="mb-5 flex flex-wrap gap-2">
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setTab(g)}
            className={
              "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all " +
              (active === g
                ? "text-white"
                : t.pillBorder + " " + t.surfaceSoft + " " + t.textMuted + " " + t.surfaceHover)
            }
            style={
              active === g
                ? {
                    background: `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.purple})`,
                    borderColor: "transparent",
                    boxShadow: `0 4px 16px ${BRAND.teal}55`,
                  }
                : undefined
            }
          >
            {g}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {list.map((m, i) => (
          <IitMeasureCard key={m.key + active} m={m} index={i} />
        ))}
      </div>
    </IitCard>
  );
}

function IitStress({ stress }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  if (!stress) return null;

  const Block = ({ title, sub, children }) => (
    <div className={"flex flex-col rounded-2xl border p-4 " + t.pillBorder + " " + t.surfaceSoft}>
      <div className={"text-sm font-bold " + t.textPrimary}>{title}</div>
      <div className={"mb-4 text-[11px] " + t.textMuted}>{sub}</div>
      {children}
    </div>
  );

  return (
    <IitCard accent="purple" icon={Activity} title="Situational stress" subtitle="What students are carrying right now">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Block title="Stress load" sub="Number of active stressors">
          <div className="flex flex-1 flex-col items-center gap-4">
            <IitDonut
              bands={stress.load.bands}
              centerValue={stress.load.avg}
              centerLabel="avg stressors"
              size={150}
            />
            <IitLegend bands={stress.load.bands} />
          </div>
        </Block>
        <Block title="Direction of stress" sub="Is it getting better or worse?">
          <IitColumns bands={stress.trend} />
        </Block>
        <Block title="Mental space it takes" sub="How much the main stressor weighs">
          <div className="flex flex-1 flex-col items-center gap-4">
            <IitDonut
              bands={stress.impact}
              centerValue={`${stress.impact[2]?.pct ?? 0}%`}
              centerLabel="consumed"
              size={150}
            />
            <IitLegend bands={stress.impact} />
          </div>
        </Block>
      </div>
    </IitCard>
  );
}

function IitParticipation({ weekly, totalSubmissions }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hv, setHv] = useState(null);

  const W = 600;
  const H = 230;
  const padX = 24;
  const padTop = 34;
  const padBottom = 34;
  const max = Math.max(...weekly.map((w) => w.count), 1);
  const stepX = weekly.length > 1 ? (W - padX * 2) / (weekly.length - 1) : 0;
  const pts = weekly.map((w, i) => ({
    x: padX + i * stepX,
    y: padTop + (1 - w.count / max) * (H - padTop - padBottom),
    ...w,
  }));

  const line = pts.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = pts[i - 1];
    const cx = (prev.x + p.x) / 2;
    return d + ` C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
  }, "");
  const area = pts.length ? `${line} L ${pts[pts.length - 1].x} ${H - padBottom} L ${pts[0].x} ${H - padBottom} Z` : "";

  return (
    <IitCard
      accent="teal"
      icon={CalendarDays}
      title="Participation"
      subtitle={`${totalSubmissions} submissions in total · last ${weekly.length} weeks`}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full">
        <defs>
          <linearGradient id="iitAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND.purple} stopOpacity="0.35" />
            <stop offset="100%" stopColor={BRAND.pink} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="iitLineStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={BRAND.pink} />
            <stop offset="100%" stopColor={BRAND.purple} />
          </linearGradient>
        </defs>
        {[0, 0.5, 1].map((g) => (
          <line
            key={g}
            x1={padX}
            x2={W - padX}
            y1={padTop + g * (H - padTop - padBottom)}
            y2={padTop + g * (H - padTop - padBottom)}
            stroke={isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"}
            strokeDasharray="4 5"
          />
        ))}
        <path d={area} fill="url(#iitAreaFill)" />
        <path
          d={line}
          fill="none"
          stroke="url(#iitLineStroke)"
          strokeWidth="3"
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${BRAND.purple}88)` }}
        />
        {pts.map((p, i) => (
          <g key={p.weekStart} onMouseEnter={() => setHv(i)} onMouseLeave={() => setHv(null)}>
            <rect x={p.x - stepX / 2} y={0} width={Math.max(stepX, 20)} height={H} fill="transparent" />
            <circle
              cx={p.x}
              cy={p.y}
              r={hv === i ? 7 : 4.5}
              fill={BRAND.purple}
              stroke="#fff"
              strokeWidth="2"
              style={{ transition: "r .15s ease", filter: hv === i ? `drop-shadow(0 0 8px ${BRAND.pink})` : "none" }}
            />
            {hv === i && (
              <text x={p.x} y={p.y - 14} textAnchor="middle" fontSize="13" fontWeight="800" fill={BRAND.purple}>
                {p.count}
              </text>
            )}
            {(i % 2 === 0 || weekly.length <= 8) && (
              <text
                x={p.x}
                y={H - 12}
                textAnchor="middle"
                fontSize="10"
                fill={isDark ? "#94a3b8" : "#64748b"}
              >
                {new Date(p.weekStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </text>
            )}
          </g>
        ))}
      </svg>
    </IitCard>
  );
}

function IitPriorityList({ students }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [showAll, setShowAll] = useState(false);
  const list = showAll ? students : students.slice(0, 8);

  return (
    <IitCard
      accent="pink"
      icon={Eye}
      title="Students for follow-up"
      subtitle="Priority and Elevated tiers, shown as anonymised references"
    >
      {students.length === 0 ? (
        <p className={"py-8 text-center text-sm " + t.textMuted}>
          No students currently in the Elevated or Priority tiers.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-1">
            {list.map((s, i) => {
              const m = IIT_TIER_META[s.tier];
              const [from, to] = IIT_GRAD[m.tone];
              return (
                <div
                  key={s.ref}
                  className={"ta-stagger rounded-2xl border p-4 " + t.pillBorder + " " + t.surfaceSoft}
                  style={{ animationDelay: `${i * 30}ms` }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-extrabold text-white"
                        style={{
                          background: `linear-gradient(135deg, ${from}, ${to})`,
                          boxShadow: `0 4px 14px ${to}66`,
                        }}
                      >
                        {s.ref.slice(0, 2)}
                      </span>
                      <div>
                        <div className={"font-mono text-sm font-bold " + t.textPrimary}>#{s.ref}</div>
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: `${to}1f`, color: to }}
                        >
                          {m.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {[
                        ["K10", s.k10],
                        ["PHQ-9", s.phq9],
                      ].map(([k, v]) => (
                        <div
                          key={k}
                          className="rounded-xl px-3 py-1.5 text-center"
                          style={{ background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
                        >
                          <div className={"text-sm font-extrabold tabular-nums " + t.textPrimary}>{v ?? "–"}</div>
                          <div className={"text-[9px] font-semibold uppercase " + t.textMuted}>{k}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.flags.length ? (
                      s.flags.map((f) => (
                        <span
                          key={f}
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{ background: `${to}14`, color: to }}
                        >
                          {f}
                        </span>
                      ))
                    ) : (
                      <span className={"text-[11px] " + t.textMuted}>Elevated screening scores</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {students.length > 8 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-5 self-center rounded-full border px-5 py-2 text-xs font-semibold transition-colors"
              style={{ borderColor: `${BRAND.purple}55`, color: BRAND.purple }}
            >
              {showAll ? "Show fewer" : `Show all ${students.length}`}
            </button>
          )}
        </>
      )}
    </IitCard>
  );
}

/* ------------------------------------------------------------------ */
/* page                                                                */
/* ------------------------------------------------------------------ */

function IitWellbeingResults({ data, loading, error }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  if (error) return <CardShell>Unable to load IIT results.</CardShell>;

  if (loading || !data) {
    return (
      <div className="flex flex-col gap-6">
        <div className={"h-56 animate-pulse rounded-3xl " + t.surfaceSoft} />
        <div className={"h-48 animate-pulse rounded-3xl " + t.surfaceSoft} />
        <div className={"h-72 animate-pulse rounded-3xl " + t.surfaceSoft} />
      </div>
    );
  }

  if (!data.totalParticipants) {
    return (
      <CardShell className="flex flex-col items-center justify-center py-14 text-gray-500">
        <GraduationCap className="mb-3 h-12 w-12 opacity-40" />
        <p className="text-lg font-semibold">No assessments yet</p>
        <p className="mt-1 text-center text-sm text-gray-400">
          Insights will appear once students complete the IIT assessment.
        </p>
      </CardShell>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <IitHero data={data} />
      <IitTierOverview tiers={data.tiers} total={data.totalParticipants} />

      <IitCard
        accent="purple"
        icon={Sparkles}
        title="Key findings"
        subtitle="Combinations of factors that matter most for outreach, highest first"
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.insights.map((ins, i) => (
            <IitInsightCard key={ins.key} insight={ins} index={i} />
          ))}
        </div>
      </IitCard>

     
      <IitScreening screening={data.screening} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <IitArchetypes archetypes={data.archetypes} />
        <IitParticipation weekly={data.weekly} totalSubmissions={data.totalSubmissions} />
      </div>

      <IitDeepDive measures={data.measures} />
      <IitStress stress={data.stress} />
      <IitPriorityList students={data.priorityStudents} />
       <IitTopConcerns measures={data.measures} />
    </div>
  );
}

export default IitWellbeingResults;